import React, { useState, useEffect, useRef, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Ticket,
  Users,
  DollarSign,
  Settings,
  LogOut,
  Plus,
  Trash2,
  Check,
  Clock,
  ChevronDown,
  Search,
  X,
  QrCode,
  Camera,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Edit,
  Download,
  FileSpreadsheet,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  FormInput,
  Calendar,
  Mail,
  Send,
  Eye,
  Menu,
  Paperclip,
  Wrench,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../context/AuthContext";
import { Html5Qrcode } from "html5-qrcode";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { SessionManagement, CouponManagement, CustomFieldsManagement } from "../../components/AdminSettings";
import RichTextEditor from "../../components/RichTextEditor";

import { SortableHeader } from "./shared/SortableHeader";
import { API_URL } from "../../config";
import { exportToPDF, exportToExcel } from "../../lib/export";
import { ColumnPickerModal } from "../../components/admin/ColumnPickerModal";
import { RESERVED_CORE_FIELD_NAMES } from "../../lib/reservedFields";

export const BuyersList = () => {
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [showPdfPicker, setShowPdfPicker] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  // Reserved core names — see /app/frontend/src/lib/reservedFields.js.
  const nonCoreCustomFields = customFields.filter((cf) => !RESERVED_CORE_FIELD_NAMES.includes(cf.name));
  const [showColsPicker, setShowColsPicker] = useState(false);
  const BUYERS_DEFAULT_COLS = ["name", "email", "phone", "church", "age", "qty", "tickets"];
  const BUYERS_COLS_STORAGE = "buyers-table-cols-v1";
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const raw = localStorage.getItem(BUYERS_COLS_STORAGE);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) return new Set(parsed);
      }
    } catch {}
    return new Set(BUYERS_DEFAULT_COLS);
  });
  const colVisible = (k) => visibleCols.has(k);
  useEffect(() => {
    try { localStorage.setItem(BUYERS_COLS_STORAGE, JSON.stringify(Array.from(visibleCols))); } catch {}
  }, [visibleCols]);

  useEffect(() => {
    fetchBuyers();
  }, []);

  const fetchBuyers = async () => {
    try {
      const [bRes, cfRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/buyers`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/custom-fields`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setBuyers(bRes.data);
      setCustomFields(Array.isArray(cfRes.data) ? cfRes.data : []);
    } catch (error) {
      console.error("Error fetching buyers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const filteredBuyers = useMemo(() => {
    const filtered = buyers.filter(
      (buyer) =>
        buyer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        buyer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (buyer.phone || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        buyer.church?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return [...filtered].sort((a, b) => {
      let aVal, bVal;
      if (sortConfig.key === "qty") {
        aVal = a.tickets?.length || 0;
        bVal = b.tickets?.length || 0;
      } else {
        aVal = a[sortConfig.key] || "";
        bVal = b[sortConfig.key] || "";
        if (typeof aVal === "string") aVal = aVal.toLowerCase();
        if (typeof bVal === "string") bVal = bVal.toLowerCase();
      }
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [buyers, searchTerm, sortConfig]);

  if (loading) {
    return <div className="animate-pulse">A carregar...</div>;
  }

  return (
    <div data-testid="buyers-list">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestão de Compradores</h1>
          <p className="text-zinc-500 text-sm mt-1">Vista geral da Tabela de Compradores</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button onClick={() => setShowColsPicker(true)} className="btn-secondary" data-testid="open-cols-picker-buyers" title="Escolher colunas visíveis">
            <Eye className="w-4 h-4 mr-2" />
            Colunas
          </Button>
          <Button onClick={() => setShowPdfPicker(true)} className="btn-secondary" data-testid="open-pdf-picker-buyers">
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <Button onClick={() => {
            const data = filteredBuyers.map(b => ({
              Nome: b.name, Email: b.email, Telefone: b.phone, Igreja: b.church, Idade: b.age, QTD: b.tickets?.length || 0, Bilhetes: b.tickets.map(t => t.ticket_number).join(", ")
            }));
            exportToExcel(data, "compradores");
          }} className="btn-secondary">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
        </div>
      </div>

      {/* Total Buyers Stat Card */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-zinc-900 border border-white/10 rounded-xl p-5" data-testid="buyers-total-card">
          <p className="text-zinc-400 text-xs uppercase tracking-wider font-semibold">Total de Compradores</p>
          <p className="text-3xl font-bold text-white mt-2">{buyers.length}</p>
          <p className="text-zinc-500 text-xs mt-1">{filteredBuyers.length} visíveis após filtros</p>
        </div>
      </div>

      {/* PDF Column Picker */}
      <ColumnPickerModal
        open={showPdfPicker}
        onClose={() => setShowPdfPicker(false)}
        columns={[
          { header: "Nome", key: "Nome" }, { header: "Email", key: "Email" }, { header: "Telefone", key: "Telefone" },
          { header: "Igreja", key: "Igreja" }, { header: "Idade", key: "Idade" }, { header: "QTD", key: "QTD" }, { header: "Bilhetes", key: "Bilhetes" },
          ...nonCoreCustomFields.map((cf) => ({ key: cf.label, header: cf.label })),
        ]}
        storageKey="pdf-cols-compradores"
        title="Exportar PDF · Compradores"
        onConfirm={(cols) => {
          const data = filteredBuyers.map(b => ({
            Nome: b.name, Email: b.email, Telefone: b.phone, Igreja: b.church, Idade: b.age,
            QTD: b.tickets?.length || 0, Bilhetes: b.tickets.map(t => t.ticket_number).join(", "),
            ...Object.fromEntries(nonCoreCustomFields.map((cf) => {
              const v = b.custom_fields?.[cf.name];
              const val = (v == null || v === "") ? "—" : (cf.field_type === "checkbox" ? (v ? "Sim" : "Não") : String(v));
              return [cf.label, val];
            })),
          }));
          exportToPDF(data, cols, "Base de Dados de Compradores", "compradores");
        }}
      />

      {/* Table Column Visibility Picker */}
      <ColumnPickerModal
        open={showColsPicker}
        onClose={() => setShowColsPicker(false)}
        columns={[
          { key: "name", header: "Nome" },
          { key: "email", header: "Email" },
          { key: "phone", header: "Telefone" },
          { key: "church", header: "Igreja" },
          { key: "age", header: "Idade" },
          { key: "qty", header: "QTD" },
          { key: "tickets", header: "Bilhetes" },
          ...nonCoreCustomFields.map((cf) => ({ key: `cf_${cf.name}`, header: `${cf.label} (campo personalizado)` })),
        ]}
        storageKey={BUYERS_COLS_STORAGE}
        title="Colunas visíveis · Compradores"
        confirmLabel="Aplicar"
        onConfirm={(cols) => { setVisibleCols(new Set(cols.map((c) => c.key))); setShowColsPicker(false); }}
      />

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
        <Input
          placeholder="Pesquisar por nome, email, telefone ou igreja..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input-dark pl-11"
          data-testid="search-buyers"
        />
      </div>

      <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-x-auto">
        <table className="table-dark min-w-[700px]">
          <thead>
            <tr>
              {colVisible("name") && <SortableHeader label="Nome" sortKey="name" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("email") && <SortableHeader label="Email" sortKey="email" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("phone") && <SortableHeader label="Telefone" sortKey="phone" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("church") && <SortableHeader label="Igreja" sortKey="church" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("age") && <SortableHeader label="Idade" sortKey="age" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("qty") && <SortableHeader label="QTD" sortKey="qty" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("tickets") && <th>Bilhetes</th>}
              {nonCoreCustomFields.map((cf) => colVisible(`cf_${cf.name}`) && (
                <th key={`th-cf-${cf.id}`} className="text-violet-200 whitespace-nowrap" title="Campo personalizado">
                  {cf.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredBuyers.map((buyer) => (
              <tr key={buyer.email} data-testid={`buyer-row-${buyer.email}`}>
                {colVisible("name") && <td className="text-white font-medium">{buyer.name}</td>}
                {colVisible("email") && <td className="text-zinc-400">{buyer.email}</td>}
                {colVisible("phone") && <td className="text-zinc-400">{buyer.phone}</td>}
                {colVisible("church") && <td className="text-zinc-400">{buyer.church}</td>}
                {colVisible("age") && <td className="text-zinc-400">{buyer.age}</td>}
                {colVisible("qty") && <td className="text-white font-semibold" data-testid={`buyer-qty-${buyer.email}`}>
                  <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 text-xs font-bold">
                    {buyer.tickets?.length || 0}
                  </span>
                </td>}
                {colVisible("tickets") && <td>
                  <div className="flex flex-wrap gap-1">
                    {buyer.tickets.map((ticket) => (
                      <span
                        key={ticket.ticket_number}
                        className={`badge text-xs ${ticket.payment_status === "paid" ? "badge-success" : "badge-warning"}`}
                      >
                        {ticket.ticket_number}
                      </span>
                    ))}
                  </div>
                </td>}
                {nonCoreCustomFields.map((cf) => colVisible(`cf_${cf.name}`) && (
                  <td key={`td-cf-${cf.id}-${buyer.email}`} className="text-zinc-300 text-sm whitespace-nowrap">
                    {(() => {
                      const v = buyer.custom_fields?.[cf.name];
                      if (v === undefined || v === null || v === "") return <span className="text-zinc-600">—</span>;
                      if (cf.field_type === "checkbox") return v ? "✓" : "—";
                      return String(v);
                    })()}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {filteredBuyers.length === 0 && (
          <div className="text-center py-12 text-zinc-500">
            Nenhum comprador encontrado
          </div>
        )}
      </div>
    </div>
  );
};

// Financial Summary
